import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Cake, Plus, Minus, X, Warning, Calendar, Clock, CheckCircle, Sparkle, Camera, Heart, InstagramLogo, FacebookLogo, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSiteSettings } from "@/context/SiteSettingsContext";

// OrderJar palette — pearl-white + raspberry + butter yellow + jet ink + chocolate accent
const PEARL = "#FDFAF6";
const PEARL_2 = "#FCE9DA";
const INK = "#1A1A1A";
const RASPBERRY = "#E6196F";
const RASPBERRY_SOFT = "#FFE4F0";
const BUTTER = "#F6C648";
const COCOA = "#5B3A2E";
const STONE = "#E5D5C9";

const TIERS = [
  { id: "s", label: "Small", slices: 8, price: 45 },
  { id: "m", label: "Medium", slices: 14, price: 75 },
  { id: "l", label: "Large", slices: 24, price: 120 },
];

const FLAVOURS = ["Vanilla bean", "Lemon drizzle", "Raspberry & pistachio", "Salted caramel", "Chocolate fudge", "Earl Grey"];
const ALLERGENS_DEFAULT = ["gluten", "dairy", "egg"];

const SEED_ORDERS = [
  { id: "ord-014", customer: "Sarah K.", occasion: "30th birthday", date: "Sat 19 Apr", collection: "13:00", tier: "m", flavour: "Raspberry & pistachio", price: 75, status: "deposit_paid", allergens: ["gluten", "dairy", "egg", "nuts"] },
  { id: "ord-015", customer: "James T.", occasion: "Christening · 30 cupcakes", date: "Sun 27 Apr", collection: "11:00", tier: "—", flavour: "Vanilla & lemon", price: 85, status: "quote_sent", allergens: ["gluten", "dairy", "egg"] },
  { id: "ord-016", customer: "Rachel M.", occasion: "Wedding · 3-tier", date: "Sat 7 Jun", collection: "10:00", tier: "l", flavour: "Vanilla / lemon / chocolate", price: 420, status: "confirmed", allergens: ["gluten", "dairy", "egg"] },
  { id: "ord-013", customer: "Priya S.", occasion: "Anniversary cupcakes", date: "Fri 11 Apr", collection: "—", tier: "—", flavour: "Salted caramel", price: 60, status: "delivered", allergens: ["gluten", "dairy", "egg"] },
  { id: "ord-017", customer: "Ola B.", occasion: "Brownies · 12", date: "Sat 19 Apr", collection: "15:30", tier: "—", flavour: "Triple chocolate", price: 36, status: "confirmed", allergens: ["gluten", "dairy", "egg"] },
];

const STATUS_SEQUENCE = ["quote_sent", "deposit_paid", "confirmed", "baking", "delivered"];

// Deposit collected at each stage (50% on deposit_paid → confirmed → baking, 100% on delivered)
const depositOf = (o) => {
  if (o.status === "quote_sent") return 0;
  if (o.status === "delivered") return o.price;
  return o.price * 0.5;
};

const STATUS_META = {
  quote_sent: { label: "Quote sent", bg: RASPBERRY, fg: "#FFFFFF" },
  deposit_paid: { label: "Deposit paid", bg: INK, fg: BUTTER },
  confirmed: { label: "Confirmed", bg: BUTTER, fg: INK },
  baking: { label: "Baking", bg: COCOA, fg: PEARL },
  delivered: { label: "Delivered", bg: STONE, fg: COCOA },
};

const TABS = [
  { key: "book", label: "Order book" },
  { key: "public", label: "Public link" },
  { key: "week", label: "This week" },
];

export default function OrderJar() {
  const { settings } = useSiteSettings();
  const [tab, setTab] = useState("book");
  const navigate = useNavigate();
  const [orders, setOrders] = useState(SEED_ORDERS);

  const addOrder = (o) => {
    setOrders((prev) => [{ ...o, id: `ord-${String(100 + prev.length).slice(-3)}` }, ...prev]);
    toast.success(`Quote sent to ${o.customer}`, { description: `${o.occasion} · ${o.date}` });
  };

  const setStatus = (id, status) => {
    setOrders((prev) => prev.map((o) => {
      if (o.id !== id) return o;
      if (status === o.status) return o;
      toast.success(`${o.customer} → ${STATUS_META[status].label}`);
      return { ...o, status };
    }));
  };

  const advance = (id) => {
    setOrders((prev) => prev.map((o) => {
      if (o.id !== id) return o;
      const i = STATUS_SEQUENCE.indexOf(o.status);
      const next = STATUS_SEQUENCE[Math.min(i + 1, STATUS_SEQUENCE.length - 1)];
      if (next === o.status) return o;
      toast.success(`${o.customer} → ${STATUS_META[next].label}`);
      return { ...o, status: next };
    }));
  };

  const finishToday = () => {
    const eligible = orders.filter((o) => ["confirmed", "baking"].includes(o.status));
    if (eligible.length === 0) {
      toast.message("Nothing to mark — no confirmed/baking orders today.");
      return;
    }
    setOrders((prev) => prev.map((o) => ["confirmed", "baking"].includes(o.status) ? { ...o, status: "delivered" } : o));
    toast.success(`Marked ${eligible.length} order${eligible.length > 1 ? "s" : ""} delivered · £${eligible.reduce((t, o) => t + o.price, 0)} into Earned`);
  };

  // Reactive financials — recompute from order state on every render
  const earned = orders.filter((o) => o.status === "delivered").reduce((t, o) => t + o.price, 0);
  const depositsHeld = orders.filter((o) => ["deposit_paid", "confirmed", "baking"].includes(o.status)).reduce((t, o) => t + o.price * 0.5, 0);
  const quotesOut = orders.filter((o) => o.status === "quote_sent").length;
  const slotsLeft = 3;

  return (
    <div className="min-h-screen" style={{ backgroundColor: PEARL, color: INK, fontFamily: "'Outfit', sans-serif" }} data-testid="orderjar-interactive">
      <header className="sticky top-0 z-40 border-b" style={{ backgroundColor: "#FFFFFF", borderColor: STONE }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/demos/orderjar" className="mr-1" style={{ color: INK, opacity: 0.5 }}><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 flex items-center justify-center" style={{ backgroundColor: RASPBERRY }}>
              <Cake size={18} weight="fill" color={BUTTER} />
            </div>
            <div>
              <div className="text-base font-bold leading-none">Lily Vanilli Bakes <span className="font-mono text-[10px] opacity-60">· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest mt-0.5" style={{ color: COCOA }}>Cottage food order book</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=orderjar")} className="text-xs font-bold px-3 py-1.5 whitespace-nowrap" style={{ backgroundColor: INK, color: BUTTER }} data-testid="orderjar-cta">
            Co-build yours <ArrowRight size={12} weight="bold" className="inline ml-1" />
          </button>
        </div>
        <nav className="max-w-6xl mx-auto px-6 flex gap-6 border-t" style={{ borderColor: STONE }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className="relative py-3 text-sm font-semibold" style={{ color: tab === t.key ? INK : COCOA }} data-testid={`orderjar-tab-${t.key}`}>
              {t.label}
              {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: RASPBERRY }} />}
            </button>
          ))}
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-8">
        {tab === "book" && <OrderBookTab orders={orders} earned={earned} depositsHeld={depositsHeld} quotesOut={quotesOut} slotsLeft={slotsLeft} onAdvance={advance} onSetStatus={setStatus} onFinishToday={finishToday} />}
        {tab === "public" && <PublicLinkTab onAdd={addOrder} />}
        {tab === "week" && <WeekTab orders={orders} />}
      </section>

      {settings.co_innovation_enabled && (
        <footer className="border-t mt-8 py-12" style={{ backgroundColor: "#FFFFFF", borderColor: STONE }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: RASPBERRY }}>Co-innovation candidate</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-4">Be the founding home baker.</h2>
            <p className="text-base mb-6 max-w-lg mx-auto" style={{ color: COCOA }}>We co-build OrderJar with one founding baker. You shape the features, you get it free for the first 6 months, and you keep using it forever at half the App Store price.</p>
            <Link to="/consultation?demo=orderjar" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold" style={{ backgroundColor: INK, color: BUTTER }} data-testid="orderjar-footer-cta">Co-build OrderJar with us <ArrowRight size={14} weight="bold" /></Link>
          </div>
        </footer>
      )}
    </div>
  );
}

// ---------- ORDER BOOK ----------
function OrderBookTab({ orders, earned, depositsHeld, quotesOut, slotsLeft, onAdvance, onSetStatus, onFinishToday }) {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>April 2026</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Your order book</h1>
        </div>
        <button onClick={onFinishToday} className="text-xs font-bold px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: BUTTER, color: INK }} data-testid="orderjar-finish-today">
          <CheckCircle size={14} weight="fill" /> Mark today's bakes done
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat label="Earned · Apr" value={`£${earned}`} bg={INK} fg={BUTTER} />
        <Stat label="Deposits held" value={`£${depositsHeld.toFixed(0)}`} bg={RASPBERRY_SOFT} fg={INK} accent={RASPBERRY} />
        <Stat label="Quotes out" value={quotesOut} bg="#FFFFFF" fg={INK} accent={COCOA} bordered />
        <Stat label="Slots left · Apr" value={slotsLeft} bg={BUTTER} fg={INK} />
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="orderjar-filters">
        {[["all", "All"], ["quote_sent", "Quotes"], ["deposit_paid", "Deposit paid"], ["confirmed", "Confirmed"], ["baking", "Baking"], ["delivered", "Delivered"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border" style={{ backgroundColor: filter === k ? INK : "transparent", color: filter === k ? PEARL : INK, borderColor: filter === k ? INK : STONE }}>{l}</button>
        ))}
      </div>

      <div className="grid gap-4">
        {filtered.map((o) => (
          <OrderRow key={o.id} order={o} onAdvance={onAdvance} onSetStatus={onSetStatus} />
        ))}
      </div>

      <div className="mt-6 p-5 border-2 border-dashed flex items-start gap-3" style={{ borderColor: RASPBERRY, backgroundColor: RASPBERRY_SOFT }}>
        <Warning size={18} weight="fill" style={{ color: RASPBERRY }} />
        <div className="text-sm" style={{ color: INK }}>
          <b>Natasha's Law check</b> — every order's allergen pills above auto-attach to the customer receipt and your bake-day prep sheet. <span style={{ color: COCOA }}>That's your cottage-food legal cover, sorted.</span>
        </div>
      </div>
    </div>
  );
}

function OrderRow({ order: o, onAdvance, onSetStatus }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const meta = STATUS_META[o.status];
  const dep = depositOf(o);
  return (
    <article className="bg-white border p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" style={{ borderColor: STONE }} data-testid={`orderjar-row-${o.id}`}>
      <div className="md:col-span-6">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h3 className="text-lg font-bold">{o.customer}</h3>
          <span className="text-xs font-mono" style={{ color: COCOA }}>· {o.id.toUpperCase()}</span>
        </div>
        <p className="text-sm mt-0.5" style={{ color: COCOA }}>{o.occasion}</p>
        <p className="text-xs font-mono mt-1.5 flex items-center gap-2" style={{ color: COCOA }}>
          <Calendar size={12} weight="bold" /> {o.date}{o.collection !== "—" ? ` · pickup ${o.collection}` : ""} · {o.flavour}
        </p>
        {o.allergens?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {o.allergens.map((a) => <span key={a} className="text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5" style={{ borderColor: RASPBERRY, color: RASPBERRY, backgroundColor: RASPBERRY_SOFT }}>{a}</span>)}
          </div>
        )}
      </div>
      <div className="md:col-span-3 text-right md:text-left">
        <div className="text-2xl font-black">£{o.price}</div>
        <div className="text-xs font-mono" style={{ color: COCOA }}>{o.status === "delivered" ? "Paid in full" : `Held: £${dep.toFixed(0)} of £${o.price}`}</div>
      </div>
      <div className="md:col-span-3 flex flex-col gap-2 items-stretch md:items-end relative">
        <button onClick={() => setMenuOpen(!menuOpen)} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 self-start md:self-end" style={{ backgroundColor: meta.bg, color: meta.fg, border: `1px solid ${meta.bg}` }} data-testid={`orderjar-status-${o.id}`}>
          {meta.label} <span style={{ opacity: 0.7 }}>▾</span>
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-7 z-10 bg-white border shadow-lg" style={{ borderColor: STONE }} data-testid={`orderjar-status-menu-${o.id}`}>
            {STATUS_SEQUENCE.map((s) => (
              <button key={s} onClick={() => { onSetStatus(o.id, s); setMenuOpen(false); }} className="block w-full text-left text-xs font-mono uppercase tracking-widest px-4 py-2 hover:bg-zinc-50 whitespace-nowrap" style={{ color: s === o.status ? RASPBERRY : INK }}>
                {STATUS_META[s].label}{s === o.status ? " ✓" : ""}
              </button>
            ))}
          </div>
        )}
        {o.status !== "delivered" && (
          <button onClick={() => onAdvance(o.id)} className="text-xs font-bold px-3 py-1.5" style={{ backgroundColor: INK, color: BUTTER }} data-testid={`orderjar-advance-${o.id}`}>
            Next stage →
          </button>
        )}
      </div>
    </article>
  );
}

function Stat({ label, value, bg, fg, accent, bordered }) {
  return (
    <div className="px-4 py-3" style={{ backgroundColor: bg, color: fg, border: bordered ? `1px solid ${STONE}` : "none" }}>
      <div className="text-[9px] font-mono uppercase tracking-widest font-bold" style={{ color: accent || fg, opacity: accent ? 1 : 0.7 }}>{label}</div>
      <div className="text-2xl md:text-3xl font-black mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

// ---------- PUBLIC LINK (customer-facing form mock) ----------
function PublicLinkTab({ onAdd }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ occasion: "", date: "Sat 19 Apr", tier: "m", flavour: "Raspberry & pistachio", customer: "" });
  const tier = TIERS.find((t) => t.id === form.tier);

  const goNext = () => setStep((s) => Math.min(s + 1, 4));
  const submit = () => {
    onAdd({
      customer: form.customer || "New customer",
      occasion: form.occasion || "Cake order",
      date: form.date,
      collection: "13:00",
      tier: form.tier,
      flavour: form.flavour,
      price: tier.price,
      status: "quote_sent",
      allergens: ALLERGENS_DEFAULT,
    });
    setStep(1);
    setForm({ occasion: "", date: "Sat 19 Apr", tier: "m", flavour: "Raspberry & pistachio", customer: "" });
    toast.success("Order in your book — go check the Order book tab");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>Customer view</p>
        <h1 className="text-3xl font-black tracking-tighter mb-4">What your customer sees in your bio.</h1>
        <p className="text-sm mb-4" style={{ color: COCOA }}>Tap a step on the right to walk it. The order lands in your book the moment they hit submit — no DM ping-pong, no missed allergens, no double-booked Saturdays.</p>
        <div className="border p-4 bg-white mb-4" style={{ borderColor: STONE }}>
          <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: RASPBERRY }}>Your link</p>
          <div className="flex items-center gap-2 mb-3">
            <p className="font-mono text-sm flex-1 truncate">orderjar.app/<b style={{ color: RASPBERRY }}>lily-vanilli</b></p>
            <button onClick={() => { navigator.clipboard?.writeText("orderjar.app/lily-vanilli"); toast.success("Link copied"); }} className="px-2 py-1 border text-xs flex items-center gap-1" style={{ borderColor: STONE, color: COCOA }} data-testid="orderjar-copy-link">
              <Copy size={11} weight="bold" /> Copy
            </button>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>Drops cleanly into</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 p-2 border" style={{ borderColor: STONE, backgroundColor: PEARL }}>
              <InstagramLogo size={18} weight="duotone" style={{ color: RASPBERRY }} />
              <span className="text-xs font-semibold">Insta bio</span>
            </div>
            <div className="flex items-center gap-2 p-2 border" style={{ borderColor: STONE, backgroundColor: PEARL }}>
              <FacebookLogo size={18} weight="duotone" style={{ color: "#1877F2" }} />
              <span className="text-xs font-semibold">Facebook</span>
            </div>
          </div>
          <p className="text-[10px] mt-3" style={{ color: COCOA }}>Insta bio · Facebook page intro · Marketplace listing · DM auto-reply · WhatsApp Business catalogue. One link, everywhere.</p>
        </div>
        <div className="border-2 p-4" style={{ borderColor: INK, backgroundColor: BUTTER }}>
          <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>Auto-share</p>
          <div className="flex gap-2">
            <button onClick={() => toast.success("Posted to your Facebook page")} className="flex-1 px-3 py-2 text-xs font-bold flex items-center justify-center gap-1.5" style={{ backgroundColor: "#1877F2", color: "#FFFFFF" }} data-testid="orderjar-share-fb">
              <FacebookLogo size={14} weight="fill" /> Share to FB
            </button>
            <button onClick={() => toast.success("Posted to your Insta story")} className="flex-1 px-3 py-2 text-xs font-bold flex items-center justify-center gap-1.5" style={{ backgroundColor: RASPBERRY, color: "#FFFFFF" }} data-testid="orderjar-share-ig">
              <InstagramLogo size={14} weight="fill" /> Share to IG
            </button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-8 flex justify-center">
        <div className="w-full max-w-md mx-auto" data-testid="orderjar-phone">
          <div className="rounded-[40px] p-3 shadow-2xl" style={{ backgroundColor: INK }}>
            <div className="rounded-[28px] overflow-hidden" style={{ backgroundColor: PEARL }}>
              {/* Brand header */}
              <div className="px-5 pt-5 pb-4" style={{ backgroundColor: RASPBERRY }}>
                <div className="text-xl font-black text-white tracking-tight">Lily Vanilli Bakes</div>
                <div className="text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: BUTTER }}>Order a cake →</div>
              </div>

              {/* Step indicator */}
              <div className="flex gap-1 px-5 pt-4">
                {[1, 2, 3, 4].map((s) => (
                  <div key={s} className="flex-1 h-1" style={{ backgroundColor: s <= step ? RASPBERRY : STONE }} />
                ))}
              </div>

              <div className="px-5 py-5 min-h-[420px]">
                {step === 1 && (
                  <div data-testid="orderjar-step-1">
                    <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>Step 1 · Occasion</p>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {["Birthday", "Wedding", "Christening", "Just because"].map((o) => (
                        <button key={o} onClick={() => setForm({ ...form, occasion: o })} className="px-3 py-3 text-sm font-semibold border-2 text-left" style={{ borderColor: form.occasion === o ? INK : STONE, backgroundColor: form.occasion === o ? INK : "transparent", color: form.occasion === o ? BUTTER : INK }}>{o}</button>
                      ))}
                    </div>
                    <button onClick={goNext} disabled={!form.occasion} className="w-full py-3 text-sm font-bold disabled:opacity-30" style={{ backgroundColor: INK, color: BUTTER }}>Continue →</button>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="orderjar-step-2">
                    <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>Step 2 · Pick a date</p>
                    <p className="text-xs mb-3" style={{ color: COCOA }}>Some dates are full — that's automatic. Booked-out Saturdays show in pink.</p>
                    <div className="grid grid-cols-7 gap-1 mb-3">
                      {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25].map((d) => {
                        const isClosed = d === 12;
                        const isAvailable = d === 19;
                        const isSelected = form.date === `Sat ${d} Apr`;
                        return (
                          <button key={d} onClick={() => isAvailable && setForm({ ...form, date: `Sat ${d} Apr` })} disabled={isClosed || !isAvailable} className="aspect-square text-xs font-bold disabled:cursor-not-allowed" style={{ backgroundColor: isClosed ? RASPBERRY : isAvailable ? (isSelected ? INK : RASPBERRY_SOFT) : "transparent", color: isClosed ? "#FFFFFF" : isAvailable ? (isSelected ? BUTTER : INK) : COCOA, opacity: !isAvailable && !isClosed ? 0.3 : 1 }}>
                            {d}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs font-mono mb-3" style={{ color: COCOA }}>
                      <b style={{ color: RASPBERRY }}>Pink = booked out.</b> 19 Apr · 1 slot left.
                    </p>
                    <button onClick={goNext} className="w-full py-3 text-sm font-bold" style={{ backgroundColor: INK, color: BUTTER }}>Continue →</button>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="orderjar-step-3">
                    <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>Step 3 · Size & flavour</p>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {TIERS.map((t) => (
                        <button key={t.id} onClick={() => setForm({ ...form, tier: t.id })} className="py-3 text-center border-2" style={{ borderColor: form.tier === t.id ? INK : STONE, backgroundColor: form.tier === t.id ? INK : "transparent", color: form.tier === t.id ? BUTTER : INK }}>
                          <div className="text-lg font-black">£{t.price}</div>
                          <div className="text-[9px] font-mono uppercase tracking-widest mt-0.5" style={{ color: form.tier === t.id ? PEARL_2 : COCOA }}>{t.slices} slices</div>
                        </button>
                      ))}
                    </div>
                    <select value={form.flavour} onChange={(e) => setForm({ ...form, flavour: e.target.value })} className="w-full p-3 text-sm border-2 mb-4" style={{ borderColor: STONE, backgroundColor: "#FFFFFF" }}>
                      {FLAVOURS.map((f) => <option key={f}>{f}</option>)}
                    </select>
                    <button onClick={goNext} className="w-full py-3 text-sm font-bold" style={{ backgroundColor: INK, color: BUTTER }}>Continue →</button>
                  </div>
                )}

                {step === 4 && (
                  <div data-testid="orderjar-step-4">
                    <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>Step 4 · You</p>
                    <input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} placeholder="Your name" className="w-full p-3 text-sm border-2 mb-3" style={{ borderColor: STONE }} />
                    <div className="p-3 mb-3" style={{ backgroundColor: RASPBERRY_SOFT, border: `1.5px solid ${RASPBERRY}` }}>
                      <p className="text-[10px] font-mono uppercase tracking-widest font-bold mb-1" style={{ color: RASPBERRY }}>⚠ Natasha's Law</p>
                      <p className="text-xs" style={{ color: INK }}>This {tier.label.toLowerCase()} cake contains <b>{ALLERGENS_DEFAULT.join(", ")}</b>. Auto-attached to your receipt.</p>
                    </div>
                    <div className="bg-white p-3 mb-3" style={{ border: `1px solid ${STONE}` }}>
                      <div className="flex justify-between text-sm mb-1"><span style={{ color: COCOA }}>{tier.label} · {tier.slices} slices</span><b>£{tier.price}</b></div>
                      <div className="flex justify-between text-sm mb-1"><span style={{ color: COCOA }}>Flavour</span><b>{form.flavour}</b></div>
                      <div className="flex justify-between text-sm mb-3"><span style={{ color: COCOA }}>Date</span><b>{form.date}</b></div>
                      <div className="border-t pt-2 flex justify-between" style={{ borderColor: STONE }}>
                        <span className="text-sm font-mono uppercase tracking-widest" style={{ color: COCOA }}>50% deposit</span>
                        <b className="text-lg" style={{ color: RASPBERRY }}>£{(tier.price / 2).toFixed(0)}</b>
                      </div>
                    </div>
                    <button onClick={submit} className="w-full py-3 text-sm font-bold flex items-center justify-center gap-2" style={{ backgroundColor: RASPBERRY, color: "#FFFFFF" }} data-testid="orderjar-submit">
                      Pay deposit & book <ArrowRight size={14} weight="bold" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- THIS WEEK ----------
function WeekTab({ orders }) {
  const days = [
    { day: "Mon 14", label: "Prep day", note: "Sponge bake-ahead · raspberry coulis", colour: PEARL_2 },
    { day: "Tue 15", label: "Quiet", note: "—", colour: PEARL },
    { day: "Wed 16", label: "Prep day", note: "Buttercream · ganache", colour: PEARL_2 },
    { day: "Thu 17", label: "Bake day", note: "James T. cupcakes start", colour: BUTTER },
    { day: "Fri 18", label: "Bake day", note: "Sarah K. layer cake · final assembly", colour: BUTTER },
    { day: "Sat 19", label: "DELIVERY", note: "2 collections", colour: RASPBERRY },
    { day: "Sun 20", label: "Rest", note: "Recovery · admin", colour: PEARL },
  ];

  // Saturday 19 Apr collection schedule — mix orders + open slots
  const saturdayOrders = orders.filter((o) => o.date === "Sat 19 Apr").sort((a, b) => (a.collection || "").localeCompare(b.collection || ""));
  const slots = ["10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "15:30", "16:00", "17:00"];
  const slotMap = saturdayOrders.reduce((m, o) => ({ ...m, [o.collection]: o }), {});

  return (
    <div>
      <div className="mb-8">
        <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: COCOA }}>Week of 14 Apr</p>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">This week's bake plan</h1>
        <p className="text-sm mt-2 max-w-xl" style={{ color: COCOA }}>Auto-built from your confirmed orders — sponge prep two days before, buttercream the day before, full assembly on the morning of delivery.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3 mb-10">
        {days.map((d) => (
          <div key={d.day} className="border p-4 min-h-[140px] flex flex-col" style={{ backgroundColor: d.colour, borderColor: STONE, color: d.colour === RASPBERRY ? "#FFFFFF" : INK }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ opacity: 0.7 }}>{d.day}</div>
            <div className="text-base font-black tracking-tight mb-2">{d.label}</div>
            <div className="text-xs leading-relaxed mt-auto" style={{ opacity: 0.85 }}>{d.note}</div>
          </div>
        ))}
      </div>

      {/* COLLECTION SLOTS — Saturday */}
      <div className="border-2 mb-10" style={{ borderColor: INK, backgroundColor: "#FFFFFF" }} data-testid="orderjar-collection-grid">
        <div className="px-5 py-4 flex items-center justify-between" style={{ backgroundColor: INK, color: BUTTER }}>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: PEARL_2 }}>Collection schedule</p>
            <p className="text-2xl font-black tracking-tight" style={{ color: BUTTER }}>Sat 19 Apr · pickups</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: PEARL_2 }}>Booked</p>
            <p className="text-2xl font-black" style={{ color: BUTTER }}>{saturdayOrders.length} <span className="text-sm font-mono" style={{ color: PEARL_2 }}>of {slots.length}</span></p>
          </div>
        </div>
        <div className="divide-y" style={{ borderColor: STONE }}>
          {slots.map((s) => {
            const order = slotMap[s];
            return (
              <div key={s} className="grid grid-cols-12 gap-3 px-5 py-3 items-center" style={{ borderColor: STONE, borderBottomWidth: "1px" }} data-testid={`orderjar-slot-${s}`}>
                <div className="col-span-2 font-mono text-sm font-bold" style={{ color: order ? INK : COCOA }}>
                  <Clock size={12} weight="bold" className="inline mr-1.5 -mt-0.5" />{s}
                </div>
                {order ? (
                  <>
                    <div className="col-span-7">
                      <p className="font-bold text-sm">{order.customer} <span className="font-normal" style={{ color: COCOA }}>· {order.occasion}</span></p>
                      <p className="text-xs font-mono mt-0.5" style={{ color: COCOA }}>{order.flavour} · £{order.price}</p>
                    </div>
                    <div className="col-span-3 text-right">
                      <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1" style={{ backgroundColor: STATUS_META[order.status].bg, color: STATUS_META[order.status].fg }}>
                        {STATUS_META[order.status].label}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="col-span-10 text-xs font-mono uppercase tracking-widest" style={{ color: COCOA, opacity: 0.5 }}>— OPEN SLOT —</div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 flex items-center justify-between" style={{ backgroundColor: PEARL }}>
          <p className="text-xs" style={{ color: COCOA }}>30-min slots · auto-spaced so no two collections overlap your packing time.</p>
          <button onClick={() => toast.success("Slot reminders queued (SMS · Sat 09:00)")} className="text-xs font-bold px-3 py-1.5" style={{ backgroundColor: BUTTER, color: INK }} data-testid="orderjar-slot-reminders">
            Send pickup reminders →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border p-5 bg-white" style={{ borderColor: STONE }}>
          <div className="flex items-center gap-2 mb-2"><Heart size={14} weight="fill" style={{ color: RASPBERRY }} /><p className="text-xs font-mono uppercase tracking-widest" style={{ color: COCOA }}>Customer pings</p></div>
          <p className="text-2xl font-black">3 reminders</p>
          <p className="text-xs mt-1" style={{ color: COCOA }}>Balance due in 3 days · auto-sent Wed 09:00</p>
        </div>
        <div className="border p-5 bg-white" style={{ borderColor: STONE }}>
          <div className="flex items-center gap-2 mb-2"><Sparkle size={14} weight="fill" style={{ color: RASPBERRY }} /><p className="text-xs font-mono uppercase tracking-widest" style={{ color: COCOA }}>Capacity</p></div>
          <p className="text-2xl font-black">Sat full</p>
          <p className="text-xs mt-1" style={{ color: COCOA }}>Public link auto-shows "Booked out" for 12 Apr & 26 Apr</p>
        </div>
        <div className="border p-5" style={{ backgroundColor: INK, color: PEARL, borderColor: INK }}>
          <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: BUTTER }}>EHO note</p>
          <p className="text-2xl font-black" style={{ color: BUTTER }}>Allergen ledger</p>
          <p className="text-xs mt-1" style={{ color: PEARL_2 }}>One-tap export of every allergen declaration this month for your kitchen audit.</p>
        </div>
      </div>
    </div>
  );
}
